/* ═══════════════════════════════════════════════════════════
   MURAL CUSTOMIZER — Phase 1+2 (canvas, crop, dimensions, materials)
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const CM_PER_INCH = 2.54;
  const SQM_TO_SQFT = 10.7639;

  /**
   * Production panel width changes with the selected mural material.
   * Prefer an explicit pricing-matrix value, while keeping the known material
   * ids/labels compatible with the existing shop metafield.
   */
  function getPanelWidthCm(material) {
    const configured = Number(material && material.panel_width_cm);
    if (Number.isFinite(configured) && configured > 0) return configured;

    const key = [material && material.id, material && material.label, material && material.code]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('tr-TR');

    if (key.includes('non-woven') || key.includes('dokusuz')) return 155;
    if (key.includes('textile') || key.includes('tekstil')) return 102;
    if (key.includes('textured') || key.includes('dokulu')) return 102;
    if (key.includes('peel') || key.includes('stick') || key.includes('folyo') || key.includes('yapışkan')) return 76;

    return 102;
  }

  function init() {
    const root = document.querySelector('[data-mural-customizer]');
    if (!root) return;

    /* ─── State ────────────────────────────────────── */
    const productData = JSON.parse(root.querySelector('[data-product-json]').textContent);
    const pricingData = JSON.parse(root.querySelector('[data-pricing-json]').textContent);

    // Locale-driven price unit: cm-based countries see prices per m², else per sq ft.
    const usesImperialPrice = (root.dataset.defaultUnit || 'cm') !== 'cm';
    const priceUnitLabel = usesImperialPrice ? '/ sq ft' : '/ m²';

    // Country/market currency rate — pricing JSON is in USD cents, multiply by
    // the active currency rate so non-USD markets see converted prices.
    // Mirrors material-size-picker.js / wall-mural-picker.js exactly.
    const currencyRate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate)
      ? parseFloat(window.Shopify.currency.rate)
      : (parseFloat(root.dataset.currencyRate) || 1);

    // Pre-select material + dimensions from URL params (set by product-page
    // picker or the Custom Murals upload page).
    let initialMaterial = pricingData.materials[0] || null;
    let initialWidthCm = 0;
    let initialHeightCm = 0;
    let initialUnit = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const urlMaterial = params.get('material');
      if (urlMaterial) {
        const found = pricingData.materials.find((m) => m.id === urlMaterial);
        if (found) initialMaterial = found;
      }
      const wCm = parseFloat(params.get('w_cm') || '');
      const hCm = parseFloat(params.get('h_cm') || '');
      if (Number.isFinite(wCm) && wCm > 0) initialWidthCm = wCm;
      if (Number.isFinite(hCm) && hCm > 0) initialHeightCm = hCm;
      const urlUnit = params.get('unit');
      if (urlUnit === 'cm' || urlUnit === 'in' || urlUnit === 'ftin') initialUnit = urlUnit;
    } catch (e) { /* noop */ }

    const state = {
      unit: initialUnit || root.dataset.defaultUnit || 'cm',     // 'cm' | 'in' | 'ftin'
      widthCm: initialWidthCm,
      heightCm: initialHeightCm,
      material: initialMaterial,
      cropX: 0.5,    // normalized [0..1] — center of crop area in image
      cropY: 0.5,
      fitMode: 'crop',
      stretchAmount: 0,
      flip: false,
      grayscale: false,
      tools: { ruler: false, panels: false, silhouette: false },
      orderComment: '',
      imageReady: false,
      imgNaturalW: productData.blankImageWidth || 0,
      imgNaturalH: productData.blankImageHeight || 0,
    };

    /* ─── DOM refs ─────────────────────────────────── */
    const els = {
      canvasWrap: root.querySelector('[data-canvas-wrap]'),
      canvas: root.querySelector('[data-canvas]'),
      crop: root.querySelector('[data-crop]'),
      cropImg: root.querySelector('[data-crop-image]'),
      panels: root.querySelector('[data-panels]'),
      hint: root.querySelector('[data-hint]'),
      dimLabelW: root.querySelector('[data-dim-label-w]'),
      dimLabelH: root.querySelector('[data-dim-label-h]'),
      rulerTop: root.querySelector('[data-ruler-top]'),
      rulerLeft: root.querySelector('[data-ruler-left]'),
      silhouette: root.querySelector('[data-silhouette]'),
      silhouetteSvg: root.querySelector('[data-silhouette-svg]'),
      silhouettePath: root.querySelector('[data-silhouette-path]'),
      silhouetteLabel: root.querySelector('[data-silhouette-label]'),
      dragTooltip: root.querySelector('[data-drag-tooltip]'),
      dragTooltipText: root.querySelector('[data-drag-tooltip-text]'),
      tools: root.querySelectorAll('[data-tool]'),
      unitBtns: root.querySelectorAll('[data-unit]'),
      inputGroups: root.querySelectorAll('[data-input-group]'),
      dimInputs: root.querySelectorAll('[data-dim]'),
      fitToggle: root.querySelector('[data-fit-toggle]'),
      fitRange: root.querySelector('[data-fit-range]'),
      fitRangeInput: root.querySelector('[data-fit-range-input]'),
      fitValue: root.querySelector('[data-fit-value]'),
      materialsContainer: root.querySelector('[data-materials]'),
      cta: root.querySelector('[data-add-to-cart]'),
      ctaPrice: root.querySelector('[data-cta-price]'),
      ctaText: root.querySelector('.customizer__cta-text'),
      confirmModal: root.querySelector('[data-confirm-modal]'),
      confirmPreview: root.querySelector('[data-confirm-preview]'),
      confirmPreviewLoading: root.querySelector('[data-confirm-preview-loading]'),
      confirmSize: root.querySelector('[data-confirm-size]'),
      confirmMaterial: root.querySelector('[data-confirm-material]'),
      confirmPrice: root.querySelector('[data-confirm-price]'),
      confirmCheck: root.querySelector('[data-confirm-check]'),
      confirmSubmit: root.querySelector('[data-confirm-submit]'),
      confirmCancels: root.querySelectorAll('[data-confirm-cancel]'),
      flipChip: root.querySelector('[data-toggle="flip"]'),
      grayscaleChip: root.querySelector('[data-toggle="grayscale"]'),
      orderToggle: root.querySelector('[data-order-toggle]'),
      orderTextarea: root.querySelector('[data-order-textarea]'),
      loader: root.querySelector('[data-loader]'),
      loaderStatus: root.querySelector('[data-loader-status]'),
      error: root.querySelector('[data-error]'),
      errorText: root.querySelector('[data-error-text]'),
      errorClose: root.querySelector('[data-error-close]'),
    };

    /* ─── Custom image (from Custom Murals page upload) ─ */
    // If ?customImage= is present, the customer uploaded their own art on the
    // Custom Murals page. Use that image instead of the product's blank_image.
    // The original Cloudinary URL is forwarded to the cart as a line-item
    // property so the production team can fetch the full-resolution file.
    let customImageUrl = null;
    let customImageFilename = null;
    let customImagePreWidth = 0;
    let customImagePreHeight = 0;
    try {
      const params = new URLSearchParams(window.location.search);
      const ci = params.get('customImage');
      if (ci) {
        customImageUrl = ci;
        customImageFilename = params.get('customFilename') || '';
        customImagePreWidth = parseInt(params.get('customW') || '0', 10) || 0;
        customImagePreHeight = parseInt(params.get('customH') || '0', 10) || 0;
        // Reset cached natural dimensions; they'll be read on image load.
        state.imgNaturalW = customImagePreWidth;
        state.imgNaturalH = customImagePreHeight;
      }
    } catch (e) { /* noop */ }

    /* ─── Update topbar pill for custom flow ───────── */
    // In the Custom Murals flow the product is a placeholder ("Custom Mural")
    // so the default topbar pill of "Custom Mural · SKU CUSTOM-MURAL" is
    // useless. Swap in the customer's filename + their uploaded thumbnail.
    if (customImageUrl) {
      try {
        const pillTitle = root.querySelector('.customizer__product-pill-title');
        const pillSku = root.querySelector('.customizer__product-pill-sku');
        const pillThumb = root.querySelector('.customizer__product-pill-thumb');
        if (pillTitle) pillTitle.textContent = 'Özel duvar kağıdınız';
        if (pillSku) {
          if (customImageFilename) {
            // Keep filename short — long ones overflow the pill.
            const short = customImageFilename.length > 28
              ? customImageFilename.slice(0, 12) + '…' + customImageFilename.slice(-12)
              : customImageFilename;
            pillSku.textContent = short;
          } else {
            pillSku.textContent = 'Yüklenen görsel';
          }
        }
        if (pillThumb) {
          pillThumb.src = customImageUrl;
          pillThumb.alt = customImageFilename || 'Görseliniz';
        }
      } catch (e) { /* noop */ }
    }

    /* ─── Image load ───────────────────────────────── */
    if (customImageUrl) {
      // Force CORS so we can read pixels for the crop-preview canvas.
      try { els.cropImg.crossOrigin = 'anonymous'; } catch (e) {}
      els.cropImg.src = customImageUrl;
    } else if (productData.blankImage) {
      els.cropImg.src = productData.blankImage;
    } else if (productData.mockupImage) {
      els.cropImg.src = productData.mockupImage;
      console.warn('[customizer] No blank image metafield — using mockup as fallback.');
    }

    els.cropImg.addEventListener('load', () => {
      if (!state.imgNaturalW || !state.imgNaturalH) {
        state.imgNaturalW = els.cropImg.naturalWidth;
        state.imgNaturalH = els.cropImg.naturalHeight;
      }
      state.imageReady = true;
      render();
    });

    /* ─── Silhouette auto-crop ─────────────────────────
       Measure the path's actual bbox so the figure fills the rendered
       area exactly (no whitespace above the head or below the feet).
       Stored on state.silhouetteAspect so renderSilhouette can size the
       container to match the cropped aspect ratio.
       Done lazily because getBBox returns 0 while the element is
       display:none. We make it briefly measurable, read the bbox, then
       restore the original style. */
    state.silhouetteAspect = 0.49; // SVG default 878:1792
    state.silhouetteCropped = false;
    function autoCropSilhouette() {
      if (state.silhouetteCropped) return;
      if (!els.silhouetteSvg || !els.silhouettePath) return;

      // Force layout temporarily so getBBox can read accurate bounds
      const wasHidden = els.silhouette.classList.contains('is-visible') === false;
      const prevVisibility = els.silhouette.style.visibility;
      const prevDisplay = els.silhouette.style.display;
      if (wasHidden) {
        els.silhouette.style.visibility = 'hidden';
        els.silhouette.style.display = 'block';
      }

      try {
        const bbox = els.silhouettePath.getBBox();
        if (bbox.width === 0 || bbox.height === 0) return;

        // Path is wrapped in <g transform="translate(0,1792) scale(0.1,-0.1)">
        // Convert path-local coords back into SVG user space:
        //   x' = x * 0.1
        //   y' = 1792 - y * 0.1
        const x1 = bbox.x * 0.1;
        const x2 = (bbox.x + bbox.width) * 0.1;
        const y1 = 1792 - (bbox.y + bbox.height) * 0.1;
        const y2 = 1792 - bbox.y * 0.1;
        const vbX = Math.min(x1, x2);
        const vbY = Math.min(y1, y2);
        const vbW = Math.abs(x2 - x1);
        const vbH = Math.abs(y2 - y1);
        if (vbW > 0 && vbH > 0) {
          els.silhouetteSvg.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
          state.silhouetteAspect = vbW / vbH;
          state.silhouetteCropped = true;
        }
      } catch (e) { /* getBBox failed — keep defaults */ }
      finally {
        if (wasHidden) {
          els.silhouette.style.visibility = prevVisibility;
          els.silhouette.style.display = prevDisplay;
        }
      }
    }
    // Try once on first paint (works in some browsers even when hidden).
    // If it doesn't take, renderSilhouette will retry the moment the
    // element becomes visible.
    requestAnimationFrame(() => requestAnimationFrame(autoCropSilhouette));

    /* ─── Background CORS preload ──────────────────────
       Pre-warm a CORS-clean copy of the source image so canvas
       export at add-to-cart time doesn't have to wait on a fresh
       network round-trip. */
    let corsImagePromise = null;
    function preloadCorsImage() {
      if (corsImagePromise) return corsImagePromise;
      const src = customImageUrl || productData.blankImage || productData.mockupImage;
      if (!src) return Promise.reject(new Error('No image source'));
      corsImagePromise = loadImageCors(src).catch((err) => {
        // Reset so we can retry later if needed
        corsImagePromise = null;
        throw err;
      });
      return corsImagePromise;
    }
    // Kick off the preload immediately (in parallel with user input)
    if (customImageUrl || productData.blankImage || productData.mockupImage) {
      // Small delay so we don't compete with the visible cropImg load
      setTimeout(() => { preloadCorsImage().catch(() => {}); }, 300);
    }

    /* ─── Materials render ─────────────────────────── */
    function renderMaterials() {
      els.materialsContainer.innerHTML = '';
      pricingData.materials.forEach((m, idx) => {
        const isPopular = m.popular === true || (!pricingData.materials.some((x) => x.popular) && idx === 0);
        const swatchUrl = m.swatch || productData.featuredImage || '';
        // Per-unit price in USD cents → convert to active market currency.
        const usdUnitCents = usesImperialPrice
          ? (m.price_per_sqm / SQM_TO_SQFT)
          : m.price_per_sqm;
        const unitPriceLabel = formatMoney(Math.round(usdUnitCents * currencyRate));
        const desc = (m.description || '').split(':').slice(1).join(':').trim() || m.description || '';
        const wrap = document.createElement('div');
        wrap.className = 'customizer__material';
        wrap.dataset.materialId = m.id;
        if (state.material && state.material.id === m.id) wrap.classList.add('is-selected');
        wrap.innerHTML = `
          <div class="customizer__material-swatch" style="${swatchUrl ? `background-image:url(${swatchUrl})` : ''}"></div>
          <div class="customizer__material-info">
            <div class="customizer__material-name">
              ${escapeHtml(m.label)}
              ${isPopular ? '<span class="customizer__badge">En Popüler</span>' : ''}
            </div>
            <div class="customizer__material-desc">${escapeHtml(desc)}</div>
            <div class="customizer__material-price">${unitPriceLabel} <small>${priceUnitLabel}</small></div>
          </div>
          <span class="customizer__material-radio"></span>
        `;
        wrap.addEventListener('click', () => {
          state.material = m;
          renderMaterials();
          render();
        });
        els.materialsContainer.appendChild(wrap);
      });
    }

    /* ─── Unit toggle ──────────────────────────────── */
    els.unitBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const newUnit = btn.dataset.unit;
        if (newUnit === state.unit) return;
        state.unit = newUnit;
        els.unitBtns.forEach(b => b.classList.toggle('is-active', b.dataset.unit === newUnit));
        els.inputGroups.forEach(g => {
          g.hidden = g.dataset.inputGroup !== newUnit;
        });
        syncInputsFromState();
        render();
      });
    });

    /* ─── Dimension inputs ─────────────────────────── */
    els.dimInputs.forEach(inp => {
      inp.addEventListener('input', readInputs);
      inp.addEventListener('blur', readInputs);
    });

    if (els.fitToggle) {
      els.fitToggle.addEventListener('change', () => {
        state.stretchAmount = els.fitToggle.checked
          ? clamp(parseInt(els.fitRangeInput && els.fitRangeInput.value, 10) || 50, 0, 100)
          : 0;
        state.fitMode = getFitMode();
        if (els.fitRange) els.fitRange.hidden = !els.fitToggle.checked;
        state.cropX = 0.5;
        state.cropY = 0.5;
        render();
      });
    }
    if (els.fitRangeInput) {
      els.fitRangeInput.addEventListener('input', () => {
        state.stretchAmount = clamp(parseInt(els.fitRangeInput.value, 10) || 0, 0, 100);
        state.fitMode = getFitMode();
        if (els.fitValue) els.fitValue.textContent = `${state.stretchAmount}%`;
        render();
      });
    }

    function readInputs() {
      const prevW = state.widthCm;
      const prevH = state.heightCm;
      if (state.unit === 'cm') {
        const parser = window.DuvarkaplaMeasurements;
        state.widthCm = parser
          ? parser.parseCm(root.querySelector('[data-dim="w_cm"]').value)
          : (parseFloat(root.querySelector('[data-dim="w_cm"]').value) || 0);
        state.heightCm = parser
          ? parser.parseCm(root.querySelector('[data-dim="h_cm"]').value)
          : (parseFloat(root.querySelector('[data-dim="h_cm"]').value) || 0);
      } else if (state.unit === 'in') {
        const wIn = parseFloat(root.querySelector('[data-dim="w_in_only"]').value) || 0;
        const hIn = parseFloat(root.querySelector('[data-dim="h_in_only"]').value) || 0;
        state.widthCm = wIn * CM_PER_INCH;
        state.heightCm = hIn * CM_PER_INCH;
      } else { // ftin
        const wFt = parseFloat(root.querySelector('[data-dim="w_ft"]').value) || 0;
        const wIn = parseFloat(root.querySelector('[data-dim="w_in"]').value) || 0;
        const hFt = parseFloat(root.querySelector('[data-dim="h_ft"]').value) || 0;
        const hIn = parseFloat(root.querySelector('[data-dim="h_in"]').value) || 0;
        state.widthCm = (wFt * 12 + wIn) * CM_PER_INCH;
        state.heightCm = (hFt * 12 + hIn) * CM_PER_INCH;
      }
      // Only re-center the crop if the wall aspect ratio actually flips between
      // x-overflow and y-overflow (or one of the dimensions becomes zero).
      // Otherwise preserve the user's current pan position so blurring an input
      // or re-typing the same value doesn't snap back to center.
      const dimsChanged = prevW !== state.widthCm || prevH !== state.heightCm;
      if (dimsChanged) {
        const prevValid = prevW > 0 && prevH > 0;
        const nowValid = state.widthCm > 0 && state.heightCm > 0;
        const imgR = getImageRatio() || 1;
        const prevAxis = prevValid ? ((prevW / prevH) >= imgR ? 'y' : 'x') : null;
        const nowAxis = nowValid ? (getUserRatio() >= imgR ? 'y' : 'x') : null;
        if (!prevValid || !nowValid || prevAxis !== nowAxis) {
          state.cropX = 0.5;
          state.cropY = 0.5;
        }
      }
      render();
    }

    function syncInputsFromState() {
      if (state.widthCm <= 0 && state.heightCm <= 0) return;
      if (state.unit === 'cm') {
        if (state.widthCm > 0) root.querySelector('[data-dim="w_cm"]').value = Math.round(state.widthCm);
        if (state.heightCm > 0) root.querySelector('[data-dim="h_cm"]').value = Math.round(state.heightCm);
      } else if (state.unit === 'in') {
        if (state.widthCm > 0) root.querySelector('[data-dim="w_in_only"]').value = Math.round(state.widthCm / CM_PER_INCH);
        if (state.heightCm > 0) root.querySelector('[data-dim="h_in_only"]').value = Math.round(state.heightCm / CM_PER_INCH);
      } else { // ftin
        if (state.widthCm > 0) {
          const wIn = state.widthCm / CM_PER_INCH;
          root.querySelector('[data-dim="w_ft"]').value = Math.floor(wIn / 12);
          root.querySelector('[data-dim="w_in"]').value = Math.round(wIn % 12);
        }
        if (state.heightCm > 0) {
          const hIn = state.heightCm / CM_PER_INCH;
          root.querySelector('[data-dim="h_ft"]').value = Math.floor(hIn / 12);
          root.querySelector('[data-dim="h_in"]').value = Math.round(hIn % 12);
        }
      }
    }

    /* ─── Toolbar (visual aids) ────────────────────── */
    els.tools.forEach(tool => {
      tool.addEventListener('click', () => {
        const name = tool.dataset.tool;
        state.tools[name] = !state.tools[name];
        tool.classList.toggle('is-active', state.tools[name]);
        render();
      });
    });

    /* ─── Flip / grayscale ─────────────────────────── */
    els.flipChip.addEventListener('click', () => {
      state.flip = !state.flip;
      els.flipChip.setAttribute('aria-pressed', state.flip);
      els.cropImg.classList.toggle('is-flipped', state.flip);
    });
    els.grayscaleChip.addEventListener('click', () => {
      state.grayscale = !state.grayscale;
      els.grayscaleChip.setAttribute('aria-pressed', state.grayscale);
      els.cropImg.classList.toggle('is-grayscale', state.grayscale);
    });

    /* ─── Order comment ────────────────────────────── */
    els.orderToggle.addEventListener('click', () => {
      els.orderTextarea.hidden = !els.orderTextarea.hidden;
      if (!els.orderTextarea.hidden) els.orderTextarea.focus();
    });
    els.orderTextarea.addEventListener('input', () => {
      state.orderComment = els.orderTextarea.value;
    });

    /* ─── Error dismiss ────────────────────────────── */
    if (els.errorClose) {
      els.errorClose.addEventListener('click', () => { els.error.hidden = true; });
    }

    /* ─── Auto-fill order comment + hi-res link from CM page ─ */
    // The custom-murals upload page stores the user's notes and (optional)
    // hi-res cloud link in sessionStorage before redirecting here. Pull them
    // in once and clear so refresh doesn't re-apply.
    let customHiresLink = '';
    try {
      const storedNotes = sessionStorage.getItem('cm-customizer-notes');
      if (storedNotes && els.orderTextarea && !els.orderTextarea.value) {
        els.orderTextarea.value = storedNotes;
        els.orderTextarea.hidden = false;
        state.orderComment = storedNotes;
        sessionStorage.removeItem('cm-customizer-notes');
      }
      const storedLink = sessionStorage.getItem('cm-customizer-hires-link');
      if (storedLink) {
        customHiresLink = storedLink;
        sessionStorage.removeItem('cm-customizer-hires-link');
      }
    } catch (e) { /* sessionStorage unavailable — fine */ }

    /* ─── Add to cart submit ───────────────────────── */
    // Add to cart now goes through a Review & approve gate so the customer
    // sees the exact print (colours + crop) before ordering — cuts the
    // "I didn't see it like that" disputes.
    els.cta.addEventListener('click', openConfirm);

    let confirmPreviewUrl = null;

    function closeConfirm() {
      if (!els.confirmModal) return;
      els.confirmModal.hidden = true;
      document.documentElement.style.overflow = '';
      if (confirmPreviewUrl) {
        URL.revokeObjectURL(confirmPreviewUrl);
        confirmPreviewUrl = null;
      }
    }

    async function openConfirm() {
      if (els.cta.disabled) return;
      if (!hasValidDimensions() || !state.material) return;
      if (!els.confirmModal) { submitCart(); return; } // fallback if markup missing

      if (els.confirmSize) {
        els.confirmSize.textContent = `${formatDimension(state.widthCm)} × ${formatDimension(state.heightCm)}`;
      }
      if (els.confirmMaterial) els.confirmMaterial.textContent = state.material.label || state.material.id;
      if (els.confirmPrice) els.confirmPrice.textContent = formatMoney(computeTotalPrice());

      // Reset the approval gate every time.
      if (els.confirmCheck) els.confirmCheck.checked = false;
      if (els.confirmSubmit) els.confirmSubmit.disabled = true;

      if (els.confirmPreview) { els.confirmPreview.hidden = true; els.confirmPreview.removeAttribute('src'); }
      if (els.confirmPreviewLoading) els.confirmPreviewLoading.hidden = false;
      els.confirmModal.hidden = false;
      document.documentElement.style.overflow = 'hidden';

      // Render the exact print preview (same generator the cart upload uses).
      try {
        const crop = computeNaturalCrop();
        const blob = await generatePreviewBlob(crop);
        if (els.confirmModal.hidden) return; // user closed it while generating
        confirmPreviewUrl = URL.createObjectURL(blob);
        if (els.confirmPreview) {
          els.confirmPreview.src = confirmPreviewUrl;
          els.confirmPreview.hidden = false;
        }
        if (els.confirmPreviewLoading) els.confirmPreviewLoading.hidden = true;
      } catch (e) {
        // Never block the sale if the preview can't render — just drop the image.
        if (els.confirmPreviewLoading) els.confirmPreviewLoading.hidden = true;
      }
    }

    if (els.confirmCheck) {
      els.confirmCheck.addEventListener('change', () => {
        if (els.confirmSubmit) els.confirmSubmit.disabled = !els.confirmCheck.checked;
      });
    }
    if (els.confirmSubmit) {
      els.confirmSubmit.addEventListener('click', () => {
        closeConfirm();
        submitCart();
      });
    }
    if (els.confirmCancels) {
      els.confirmCancels.forEach((b) => b.addEventListener('click', closeConfirm));
    }
    // "Not quite right?" → close the review modal and open the Design request form.
    const confirmRequest = root.querySelector('[data-confirm-request]');
    if (confirmRequest) {
      confirmRequest.addEventListener('click', () => {
        closeConfirm();
        const cm = document.getElementById('ContactModal');
        if (cm && typeof cm.showModal === 'function') {
          cm.showModal();
        } else if (cm) {
          cm.setAttribute('open', '');
        }
      });
    }
    if (els.confirmModal) {
      els.confirmModal.addEventListener('click', (e) => {
        if (e.target === els.confirmModal) closeConfirm();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !els.confirmModal.hidden) closeConfirm();
      });
    }

    /* ─── Drag handler (single-axis, fit-one-dim) ─── */
    setupDrag();

    function setupDrag() {
      let dragging = false;
      let startX = 0, startY = 0;
      let startCropX = 0, startCropY = 0;

      function onDown(e) {
        if (els.crop.classList.contains('is-locked')) return;
        if (!hasValidDimensions()) return;
        dragging = true;
        const point = getPoint(e);
        startX = point.x;
        startY = point.y;
        startCropX = state.cropX;
        startCropY = state.cropY;
        els.crop.classList.add('is-dragging');
        e.preventDefault();
      }

      function onMove(e) {
        if (!dragging) return;
        const point = getPoint(e);
        const dxPx = point.x - startX;
        const dyPx = point.y - startY;

        const cropRect = els.crop.getBoundingClientRect();
        const imgRect = els.cropImg.getBoundingClientRect();
        const overflowX = imgRect.width - cropRect.width;
        const overflowY = imgRect.height - cropRect.height;

        const axis = getDragAxis();
        if (axis === 'x' && overflowX > 0) {
          state.cropX = clamp(startCropX - dxPx / overflowX, 0, 1);
        } else if (axis === 'y' && overflowY > 0) {
          state.cropY = clamp(startCropY - dyPx / overflowY, 0, 1);
        }
        renderImagePosition();
      }

      function onUp() {
        if (!dragging) return;
        dragging = false;
        els.crop.classList.remove('is-dragging');
      }

      els.crop.addEventListener('mousedown', onDown);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      els.crop.addEventListener('touchstart', onDown, { passive: false });
      window.addEventListener('touchmove', (e) => {
        if (dragging) { e.preventDefault(); onMove(e); }
      }, { passive: false });
      window.addEventListener('touchend', onUp);
    }

    function getPoint(e) {
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    }

    /* ─── Crop math ────────────────────────────────── */
    function hasValidDimensions() {
      return state.widthCm >= 30 && state.heightCm >= 30;
    }

    function getUserRatio() { return state.widthCm / state.heightCm; }
    function getImageRatio() {
      if (!state.imgNaturalW || !state.imgNaturalH) return 16 / 9;
      return state.imgNaturalW / state.imgNaturalH;
    }
    function getFitMode() {
      if (state.stretchAmount <= 0) return 'crop';
      if (state.stretchAmount >= 100) return 'stretch';
      return 'mixed';
    }

    /**
     * Drag axis (max-coverage crop):
     *   wallR ≥ imgR (wall wider) → fit image WIDTH → image taller than crop → drag Y
     *   wallR < imgR (wall taller) → fit image HEIGHT → image wider than crop → drag X
     */
    function getDragAxis() {
      if (!state.imageReady || !hasValidDimensions()) return null;
      if (state.fitMode === 'stretch') return null;
      const userR = getUserRatio();
      const imgR = getImageRatio();
      const epsilon = 0.001;
      if (Math.abs(userR - imgR) < epsilon) return null;
      return userR > imgR ? 'y' : 'x';
    }

    /**
     * Compute displayed image size for max-coverage crop.
     *   wallR ≥ imgR: fit width → image is taller than crop (overflow Y)
     *   wallR < imgR: fit height → image is wider than crop (overflow X)
     */
    function computeImageDisplay(cropDisplayW, cropDisplayH) {
      const userR = getUserRatio();
      const imgR = getImageRatio();
      let scale;
      if (userR >= imgR) {
        scale = cropDisplayW / state.imgNaturalW;
      } else {
        scale = cropDisplayH / state.imgNaturalH;
      }
      const coverW = state.imgNaturalW * scale;
      const coverH = state.imgNaturalH * scale;
      const mix = clamp(state.stretchAmount / 100, 0, 1);
      return {
        imgW: coverW + (cropDisplayW - coverW) * mix,
        imgH: coverH + (cropDisplayH - coverH) * mix,
      };
    }

    /* ─── Layout / render ──────────────────────────── */
    function render() {
      renderCanvasSize();
      renderImagePosition();
      renderPanels();
      renderDimensionLabels();
      renderHint();
      renderSilhouette();
      renderCTA();
      renderDragTooltip();
      renderToolAvailability();
    }

    function renderToolAvailability() {
      // Ruler/panels/silhouette only make sense when dimensions are entered.
      const valid = hasValidDimensions();
      els.tools.forEach(tool => {
        tool.disabled = !valid;
        // Visual active state only when dimensions valid; preserve internal state.tools so
        // the tool remembers user preference across dimension changes.
        const isOn = valid && !!state.tools[tool.dataset.tool];
        tool.classList.toggle('is-active', isOn);
      });
    }

    function getStageDimensions() {
      const wrapW = els.canvasWrap.clientWidth || window.innerWidth;
      const wrapH = els.canvasWrap.clientHeight || window.innerHeight * 0.6;
      // Silhouette is anchored to right edge of wrap; reserve enough space (mirrored due to centering).
      const horizPad = state.tools.silhouette ? 320 : 60;
      const maxW = Math.max(wrapW - horizPad, 200);
      const maxH = Math.max(wrapH - 60, 240);
      return { maxW, maxH };
    }

    function renderCanvasSize() {
      const { maxW, maxH } = getStageDimensions();
      const ratio = hasValidDimensions() ? getUserRatio() : (getImageRatio() || 1.6);

      let w = maxW;
      let h = w / ratio;
      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }

      els.canvas.style.width = `${w}px`;
      els.canvas.style.height = `${h}px`;
    }

    function renderImagePosition() {
      if (!state.imageReady) return;
      const cropW = els.crop.clientWidth;
      const cropH = els.crop.clientHeight;
      if (cropW <= 0 || cropH <= 0) return;

      const { imgW, imgH } = computeImageDisplay(cropW, cropH);

      els.cropImg.style.width = `${imgW}px`;
      els.cropImg.style.height = `${imgH}px`;

      const overflowX = imgW - cropW;
      const overflowY = imgH - cropH;

      const offsetX = -overflowX * state.cropX;
      const offsetY = -overflowY * state.cropY;

      els.cropImg.style.left = `${offsetX}px`;
      els.cropImg.style.top = `${offsetY}px`;

      // Lock state if no overflow
      const axis = getDragAxis();
      els.crop.classList.toggle('is-locked', !axis || !hasValidDimensions());
    }

    function renderPanels() {
      els.panels.innerHTML = '';
      const visible = state.tools.panels && hasValidDimensions();
      els.panels.classList.toggle('is-visible', visible);
      if (!visible) return;

      const cropW = els.crop.clientWidth || els.canvas.clientWidth;
      if (cropW <= 0) return;
      const pxPerCm = cropW / state.widthCm;
      const isImperial = state.unit !== 'cm';
      const panelWidthCm = getPanelWidthCm(state.material);
      const fullLabel = isImperial
        ? `${(panelWidthCm / CM_PER_INCH).toFixed(1)} in`
        : `${Math.round(panelWidthCm)} cm`;

      // Cut positions in cm (interior cuts only).
      const cuts = [];
      for (let cm = panelWidthCm; cm < state.widthCm; cm += panelWidthCm) cuts.push(cm);

      // Dashed cut lines.
      cuts.forEach(cm => {
        const line = document.createElement('div');
        line.className = 'customizer__panel-line';
        const s = line.style;
        s.position = 'absolute';
        s.top = '0';
        s.bottom = '0';
        s.left = `${cm * pxPerCm}px`;
        s.width = '0';
        s.borderLeft = '1.5px dashed #ffffff';
        s.filter = 'drop-shadow(0 0 1px rgba(20, 33, 61, 0.95)) drop-shadow(0 0 0.5px rgba(20, 33, 61, 0.95))';
        s.zIndex = '7';
        s.pointerEvents = 'none';
        s.display = 'block';
        els.panels.appendChild(line);
      });

      // Width labels centered in each section.
      const sections = [0, ...cuts, state.widthCm];
      for (let i = 0; i < sections.length - 1; i++) {
        const startCm = sections[i];
        const endCm = sections[i + 1];
        const sectionCm = endCm - startCm;
        const isFull = Math.abs(sectionCm - panelWidthCm) < 0.5;
        let text;
        if (isFull) {
          text = fullLabel;
        } else if (isImperial) {
          const inVal = sectionCm / CM_PER_INCH;
          text = `${inVal.toFixed(1)} in`;
        } else {
          text = `${Math.round(sectionCm)} cm`;
        }
        const label = document.createElement('div');
        label.className = 'customizer__panel-label';
        const s = label.style;
        s.position = 'absolute';
        s.left = `${((startCm + endCm) / 2) * pxPerCm}px`;
        s.top = '10px';
        s.transform = 'translateX(-50%)';
        s.background = 'rgba(20, 33, 61, 0.92)';
        s.color = '#fff';
        s.padding = '3px 9px';
        s.borderRadius = '999px';
        s.fontSize = '0.7rem';
        s.fontWeight = '600';
        s.zIndex = '8';
        s.pointerEvents = 'none';
        s.whiteSpace = 'nowrap';
        s.fontFamily = 'inherit';
        label.textContent = text;
        els.panels.appendChild(label);
      }
    }

    function renderDimensionLabels() {
      // Pills shown when dimensions valid AND ruler tool is OFF.
      const showPills = !state.tools.ruler && hasValidDimensions();
      els.dimLabelW.classList.toggle('is-visible', showPills);
      els.dimLabelH.classList.toggle('is-visible', showPills);
      if (showPills) {
        els.dimLabelW.textContent = formatDimension(state.widthCm);
        els.dimLabelH.textContent = formatDimension(state.heightCm);
      }
      renderRuler();
    }

    function renderRuler() {
      const visible = state.tools.ruler && hasValidDimensions();
      els.rulerTop.classList.toggle('is-visible', visible);
      els.rulerLeft.classList.toggle('is-visible', visible);
      els.rulerTop.innerHTML = '';
      els.rulerLeft.innerHTML = '';
      if (!visible) return;

      const cropW = els.crop.clientWidth;
      const cropH = els.crop.clientHeight;
      buildRuler(els.rulerTop, state.widthCm, cropW, 'horizontal');
      buildRuler(els.rulerLeft, state.heightCm, cropH, 'vertical');
    }

    function buildRuler(container, totalCm, totalPx, axis) {
      // Major ticks every meter (100cm) or foot (30.48cm) when imperial.
      // Minor ticks every 10cm (or 1in).
      const isImperial = state.unit !== 'cm';
      const majorCm = isImperial ? 30.48 : 100;     // 1ft or 1m
      const minorCm = isImperial ? 2.54 : 10;       // 1in or 10cm
      const majorLabel = isImperial ? 'ft' : 'm';

      // Minor ticks
      for (let cm = 0; cm <= totalCm + 0.01; cm += minorCm) {
        const px = (cm / totalCm) * totalPx;
        const isMajor = Math.abs((cm % majorCm)) < 0.5 || Math.abs((cm % majorCm) - majorCm) < 0.5;
        const tick = document.createElement('div');
        tick.className = 'customizer__ruler-tick' + (isMajor ? ' is-major' : '');
        if (axis === 'horizontal') {
          tick.style.left = `${px}px`;
        } else {
          tick.style.top = `${px}px`;
        }
        container.appendChild(tick);

        if (isMajor) {
          const label = document.createElement('span');
          label.className = 'customizer__ruler-label';
          const value = isImperial ? Math.round(cm / 30.48) : Math.round(cm / 100);
          label.textContent = `${value} ${majorLabel}`;
          if (axis === 'horizontal') {
            label.style.left = `${px}px`;
          } else {
            label.style.top = `${px}px`;
          }
          container.appendChild(label);
        }
      }
    }

    function renderHint() {
      els.hint.classList.toggle('is-hidden', hasValidDimensions());
    }

    function renderSilhouette() {
      const visible = state.tools.silhouette && hasValidDimensions();
      els.silhouette.classList.toggle('is-visible', visible);
      if (!visible) return;
      // First time it becomes visible, try to auto-crop the SVG viewBox
      // (browsers that return 0 from getBBox while display:none will now succeed)
      if (!state.silhouetteCropped) autoCropSilhouette();
      const cropH = els.crop.clientHeight;
      const wrapH = els.canvasWrap.clientHeight;
      if (cropH <= 0) return;
      const cmPerPixel = state.heightCm / cropH;
      const silhouetteHeightPx = Math.min(165 / cmPerPixel, cropH);
      els.silhouette.style.height = `${silhouetteHeightPx}px`;
      els.silhouette.style.width = `${silhouetteHeightPx * state.silhouetteAspect}px`;
      // Anchor silhouette to right edge of canvas-wrap, aligned to canvas bottom.
      els.silhouette.style.right = '12px';
      els.silhouette.style.bottom = `${(wrapH - cropH) / 2}px`;
      // Human height conventions:
      //   metric    → 165 cm
      //   imperial  → 5'5"  (Americans say height as feet+inches, not raw inches)
      if (els.silhouetteLabel) {
        els.silhouetteLabel.textContent =
          state.unit === 'cm' ? '165 cm' : `5' 5"`;
      }
    }

    function renderCTA() {
      const valid = hasValidDimensions() && !!state.material;
      els.cta.disabled = !valid;
      if (valid) {
        const total = computeTotalPrice();
        els.ctaPrice.textContent = formatMoney(total);
      }
    }

    function renderDragTooltip() {
      const axis = getDragAxis();
      const visible = hasValidDimensions() && !!axis;
      els.dragTooltip.classList.toggle('is-visible', visible);
      if (visible) {
        const dirText = axis === 'x' ? 'sağa-sola' : 'yukarı-aşağı';
        els.dragTooltipText.textContent = `Görseli ${dirText} sürükleyerek konumlandırabilirsiniz`;
      }
    }

    /* ─── Pricing ──────────────────────────────────── */
    function computeAreaSqM() {
      return (state.widthCm / 100) * (state.heightCm / 100);
    }

    function computeUsdCents() {
      // Canonical price in USD cents (market-independent). Stored as
      // _custom_price_usd so the cart can re-convert to the active market on
      // every view (see cart-currency-sync.js).
      if (!state.material) return 0;
      return Math.round(state.material.price_per_sqm * computeAreaSqM());
    }

    function computeTotalPrice() {
      // pricing: price_per_sqm in USD cents → multiply by area, then convert
      // to the active market currency via currencyRate.
      return Math.round(computeUsdCents() * currencyRate); // local-currency cents
    }

    /* ─── Helpers ──────────────────────────────────── */
    function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
    function escapeHtml(s) {
      return String(s || '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
    }

    function formatDimension(cm) {
      if (state.unit === 'cm') return `${Math.round(cm)} cm`;
      const totalIn = cm / CM_PER_INCH;
      if (state.unit === 'in') {
        return `${Math.round(totalIn)} in`;
      }
      // ftin
      const ft = Math.floor(totalIn / 12);
      const inches = Math.round(totalIn % 12);
      return `${ft} ft ${inches} in`;
    }

    function formatMoney(cents) {
      const amount = cents / 100;
      const code = root.dataset.currencyCode || 'USD';
      try {
        const digits = (Math.round(amount) === amount) ? 0 : 2;
        return `${amount.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}\u20BA`;
      } catch (e) {
        return `${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}\u20BA`;
      }
    }

    /* ─── Cart submission ──────────────────────────── */
    async function submitCart() {
      if (els.cta.disabled) return;
      if (!hasValidDimensions() || !state.material) return;

      hideError();
      setBusy(true);
      showLoader('Tasarımınız hazırlanıyor…');

      try {
        // Build crop coordinates in original image pixel space.
        const crop = computeNaturalCrop();

        // Generate preview JPEG blob (~600px wide).
        setLoaderStatus('Önizleme görseli oluşturuluyor…');
        const previewBlob = await generatePreviewBlob(crop);
        const previewFile = new File(
          [previewBlob],
          `${productData.handle || 'mural'}-preview.jpg`,
          { type: 'image/jpeg' }
        );

        // Build form data.
        const formData = new FormData();
        formData.append('id', String(productData.variantId));
        formData.append('quantity', '1');

        // ── Build size summary ────────────────────────────────────────────
        const sizeUnit = (state.unit || 'in').toUpperCase();
        const sizeW = state.unit === 'cm'
          ? Math.round(state.widthCm * 10) / 10
          : Math.round((state.widthCm / 2.54) * 10) / 10;
        const sizeH = state.unit === 'cm'
          ? Math.round(state.heightCm * 10) / 10
          : Math.round((state.heightCm / 2.54) * 10) / 10;
        const sizeStr = `${formatDimension(state.widthCm)} × ${formatDimension(state.heightCm)}`;

        // Visible properties (shown to customer in cart/order — top-level admin UI).
        // We intentionally expose Size as a SINGLE combined property so it shows
        // at the top of the order line item in both desktop and mobile Shopify
        // admin without needing to expand a sub-panel.
        formData.append('properties[Size]', sizeStr);
        formData.append('properties[Material]', state.material.label || state.material.id);
        if (state.orderComment.trim()) {
          formData.append('properties[Order Comment]', state.orderComment.trim());
        }

        // Hidden material/size codes (admin-only, used by production fulfillment).
        // Mirrors the legacy wall-mural-picker format so order workflows stay consistent.
        const materialCode = (state.material && state.material.code)
          || (state.material && state.material.label)
          || (state.material && state.material.id)
          || '';
        formData.append('properties[_Material code]', materialCode);
        formData.append('properties[_Size code]', `${sizeUnit}-${sizeW}x${sizeH}`);
        formData.append('properties[_width]', formatDimension(state.widthCm));
        formData.append('properties[_height]', formatDimension(state.heightCm));
        const panelWidthCm = getPanelWidthCm(state.material);
        formData.append('properties[_panel_width]', `${panelWidthCm} cm`);
        formData.append('properties[_panel_count]', String(Math.ceil(state.widthCm / panelWidthCm)));

        // Hidden properties (underscore prefix — admin-only, used by production).
        formData.append('properties[_area_sqm]', computeAreaSqM().toFixed(3));
        formData.append('properties[_unit]', state.unit);
        formData.append('properties[_crop_x_px]', String(crop.x));
        formData.append('properties[_crop_y_px]', String(crop.y));
        formData.append('properties[_crop_w_px]', String(crop.w));
        formData.append('properties[_crop_h_px]', String(crop.h));
        formData.append('properties[_image_w_px]', String(state.imgNaturalW));
        formData.append('properties[_image_h_px]', String(state.imgNaturalH));
        formData.append('properties[_fit_mode]', state.fitMode);
        formData.append('properties[_stretch_amount]', `${state.stretchAmount}%`);
        formData.append('properties[_flip]', state.flip ? 'yes' : 'no');
        formData.append('properties[_grayscale]', state.grayscale ? 'yes' : 'no');
        formData.append('properties[_custom_price]', String(computeTotalPrice()));

        // Custom-mural flow: forward the customer-uploaded image so the
        // production team can fetch the full-resolution original. Underscore
        // prefix hides the URL from the customer-facing cart line items but
        // remains visible in the Shopify admin order detail.
        if (customImageUrl) {
          formData.append('properties[_custom_image_url]', customImageUrl);
          if (customImageFilename) {
            formData.append('properties[Image]', customImageFilename);
            formData.append('properties[_custom_filename]', customImageFilename);
          }
          formData.append('properties[_is_custom_mural]', 'yes');
          // Hi-res original link (Drive/Dropbox/WeTransfer) — production
          // uses this for the actual print when the uploaded JPG is a
          // compressed version.
          if (customHiresLink) {
            formData.append('properties[_hires_original_link]', customHiresLink);
          }
        }

        // Preview file (generated client-side from canvas) — uploaded ONCE.
        // After cart/add we re-set its URL as a plain TEXT property below so
        // it reliably shows at the top level of the Shopify admin order.
        formData.append('properties[Crop URL]', previewFile, previewFile.name);

        setLoaderStatus('Sepetinize ekleniyor…');
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
          body: formData,
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Sepete eklenemedi.');
        }

        // Convert "Crop URL" from a file-upload property into a plain TEXT
        // property. File-type line item properties can be hidden inside the
        // bundle "Part of:" expansion in the Shopify admin order page — a text
        // URL always shows at the top level. We always run this so the admin
        // reliably sees the crop link.
        try {
          const props = (result && result.properties) || {};
          let cropUrl = props['Crop URL'];
          // Fallback: find any cdn.shopify.com URL among the properties.
          if (!cropUrl || typeof cropUrl !== 'string' || cropUrl.indexOf('http') !== 0) {
            for (const k of Object.keys(props)) {
              const v = props[k];
              if (typeof v === 'string' && v.indexOf('http') === 0 && v.indexOf('cdn.shop') >= 0) {
                cropUrl = v;
                break;
              }
            }
          }
          if (cropUrl) {
            const merged = Object.assign({}, props, { 'Crop URL': cropUrl });
            await fetch(window.Shopify.routes.root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
              body: JSON.stringify({ id: result.key, properties: merged }),
            });
          }
        } catch (e) { console.warn('[customizer] crop url patch failed:', e); }

        // Also write the customer's comment to Shopify's real cart note.
        // Keep the line-item "Order Comment" property above for production,
        // but preserve any note that may already exist in the cart.
        try {
          const noteText = state.orderComment.trim();
          if (noteText) {
            let existingNote = '';
            const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
              headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (cartResponse.ok) {
              const cartData = await cartResponse.json();
              existingNote = String(cartData.note || '').trim();
            }

            const mergedNote = !existingNote || existingNote.includes(noteText)
              ? (existingNote || noteText)
              : `${existingNote}\n\n${noteText}`;

            const noteResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ note: mergedNote }),
            });
            if (!noteResponse.ok) throw new Error('Cart note could not be updated.');
          }
        } catch (e) { console.warn('[customizer] cart note update failed:', e); }

        fireAnalytics(result);

        // Navigate to cart page (customizer layout has no cart drawer).
        setLoaderStatus('Sepetinize yönlendiriliyorsunuz…');
        window.location.href = window.Shopify.routes.root + 'cart';
      } catch (err) {
        console.error('[customizer] add to cart error:', err);
        showError(err && err.message ? err.message : 'Bir şeyler ters gitti. Lütfen tekrar deneyin.');
        hideLoader();
        setBusy(false);
      }
    }

    function computeNaturalCrop() {
      const userR = getUserRatio();
      const imgR = getImageRatio();
      // When the image is visually mirrored via CSS scaleX(-1), the same
      // cropX value points to the OPPOSITE x position in the source image.
      // Invert it so the saved crop matches what the customer sees on screen.
      const effectiveCropX = state.flip ? 1 - state.cropX : state.cropX;
      let coverW, coverH;
      if (userR >= imgR) {
        coverW = userR;
        coverH = userR / imgR;
      } else {
        coverW = imgR;
        coverH = 1;
      }
      const mix = clamp(state.stretchAmount / 100, 0, 1);
      const displayW = coverW + (userR - coverW) * mix;
      const displayH = coverH + (1 - coverH) * mix;
      const visibleX = Math.min(1, userR / displayW);
      const visibleY = Math.min(1, 1 / displayH);
      const w = state.imgNaturalW * visibleX;
      const h = state.imgNaturalH * visibleY;
      const x = (state.imgNaturalW - w) * effectiveCropX;
      const y = (state.imgNaturalH - h) * state.cropY;
      return {
        x: Math.round(x),
        y: Math.round(y),
        w: Math.round(w),
        h: Math.round(h),
      };
    }

    async function generatePreviewBlob(crop) {
      // Render the preview at the crop's native resolution (capped) instead of
      // a fixed 600px — keeps it sharp in the cart lightbox and for production.
      const MAX_PREVIEW_W = 2400;
      const targetW = Math.min(MAX_PREVIEW_W, Math.max(600, Math.round(crop.w)));
      const targetH = Math.max(1, Math.round(targetW * (state.heightCm / state.widthCm)));

      // Use cached CORS image if preloaded; otherwise load now.
      const img = await preloadCorsImage();

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      if (state.flip) {
        ctx.translate(targetW, 0);
        ctx.scale(-1, 1);
      }
      if (state.grayscale && typeof ctx.filter !== 'undefined') {
        ctx.filter = 'grayscale(1)';
      }

      ctx.drawImage(
        img,
        crop.x, crop.y, crop.w, crop.h,
        0, 0, targetW, targetH
      );

      // Manual grayscale fallback if filter not supported.
      if (state.grayscale && typeof ctx.filter === 'undefined') {
        applyGrayscaleManually(ctx, targetW, targetH);
      }

      return await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Önizleme oluşturulamadı'))),
          'image/jpeg',
          0.92
        );
      });
    }

    function loadImageCors(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Önizleme için görsel yüklenemedi'));
        img.src = src;
      });
    }

    function applyGrayscaleManually(ctx, w, h) {
      try {
        const data = ctx.getImageData(0, 0, w, h);
        const px = data.data;
        for (let i = 0; i < px.length; i += 4) {
          const g = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
          px[i] = px[i + 1] = px[i + 2] = g;
        }
        ctx.putImageData(data, 0, 0);
      } catch (e) {}
    }

    function fireAnalytics(cartItem) {
      try {
        if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
          window.Shopify.analytics.publish('product_added_to_cart', {
            cartLine: {
              merchandise: {
                product: {
                  id: String(productData.id),
                  title: productData.title,
                  vendor: '',
                  type: 'Wall Mural',
                },
              },
              quantity: cartItem.quantity || 1,
              cost: {
                totalAmount: {
                  amount: (cartItem.price || 0) / 100,
                  currencyCode: (window.Shopify.currency && window.Shopify.currency.active) || 'USD',
                },
              },
            },
          });
        }
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['track', 'Added to Cart', {
            '$value': (cartItem.price || 0) / 100,
            'ProductName': cartItem.product_title || productData.title,
            'ProductID': cartItem.product_id || productData.id,
            'VariantID': cartItem.variant_id || productData.variantId,
            'Quantity': cartItem.quantity || 1,
            'ImageURL': (cartItem.featured_image && cartItem.featured_image.url) || productData.featuredImage || '',
            'ProductURL': window.location.origin + (productData.url || ''),
            'Material': state.material && (state.material.label || state.material.id),
            'Width': formatDimension(state.widthCm),
            'Height': formatDimension(state.heightCm),
          }]);
        }
      } catch (e) { /* analytics is best-effort */ }
    }

    function showLoader(initialMsg) {
      if (els.loader) els.loader.hidden = false;
      if (initialMsg) setLoaderStatus(initialMsg);
    }
    function hideLoader() { if (els.loader) els.loader.hidden = true; }
    function setLoaderStatus(msg) {
      if (!els.loaderStatus) return;
      // Brief fade so consecutive messages feel like progress, not flicker
      els.loaderStatus.style.opacity = '0';
      setTimeout(() => {
        els.loaderStatus.textContent = msg;
        els.loaderStatus.style.opacity = '1';
      }, 120);
    }
    let _ctaOriginalText = '';
    function setBusy(busy) {
      if (!els.cta) return;
      if (busy) {
        if (els.ctaText && !_ctaOriginalText) _ctaOriginalText = els.ctaText.textContent;
        els.cta.classList.add('is-busy');
        els.cta.disabled = true;
        if (els.ctaText) els.ctaText.textContent = 'Ekleniyor…';
      } else {
        els.cta.classList.remove('is-busy');
        els.cta.disabled = false;
        if (els.ctaText && _ctaOriginalText) els.ctaText.textContent = _ctaOriginalText;
      }
    }
    function showError(msg) {
      if (!els.error) return;
      if (els.errorText) els.errorText.textContent = msg;
      els.error.hidden = false;
    }
    function hideError() { if (els.error) els.error.hidden = true; }

    /* ─── Init ─────────────────────────────────────── */
    renderMaterials();
    // Sync visible input group + unit button to state.unit (from URL param)
    els.unitBtns.forEach(b => b.classList.toggle('is-active', b.dataset.unit === state.unit));
    els.inputGroups.forEach(g => { g.hidden = g.dataset.inputGroup !== state.unit; });
    syncInputsFromState();

    // Listen to resize
    window.addEventListener('resize', debounce(render, 100));

    // Initial render after image loads. Render once now in case image is cached.
    if (els.cropImg.complete && els.cropImg.naturalWidth) {
      state.imageReady = true;
      if (!state.imgNaturalW) state.imgNaturalW = els.cropImg.naturalWidth;
      if (!state.imgNaturalH) state.imgNaturalH = els.cropImg.naturalHeight;
    }
    render();
    requestAnimationFrame(render);  // Re-render after layout settles

    function debounce(fn, delay) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, arguments), delay);
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
